import { AgentChat, type AgentChatPromptInputProps, deriveSubjectBannerMode } from "@repo/agent-chat";
import { usePatient } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { useParams } from "@tanstack/react-router";
import { getPatientName } from "@/shared/lib/patient-utils";
import { useAgentChatContext } from "./use-agent-chat-context";

function usePatientLabel(patientId: string | null | undefined): string | null {
  const { patient } = usePatient(patientId ?? undefined);
  return patient ? getPatientName(patient) : null;
}

/**
 * Wraps the chat input with the subject banner. Surfaces the pinned patient and,
 * on a wrong-patient mismatch, offers to start a fresh chat about the patient the
 * clinician is currently viewing. Names resolve client-side (zero PHI at rest).
 */
export function AgentChatSubjectBanner({ placeholder, footer }: Omit<AgentChatPromptInputProps, "banner"> = {}) {
  const { state, actions } = useAgentChatContext();
  const params = useParams({ strict: false }) as { patientId?: string };

  const subjectPatientId = state.subjectPatientId;
  const ambientPatientId = params.patientId ?? null;
  const mode = deriveSubjectBannerMode(subjectPatientId, ambientPatientId);

  const subjectLabel = usePatientLabel(subjectPatientId);
  // Only resolve the ambient patient when it's the one we'd offer to switch to.
  const ambientLabel = usePatientLabel(mode === "mismatch" ? ambientPatientId : null);

  const banner =
    mode === "none" ? undefined : mode === "mismatch" ? (
      <>
        <span className="min-w-0 flex-1 truncate">
          This chat is about <span className="text-foreground">{subjectLabel ?? "another patient"}</span>.
        </span>
        <Button className="shrink-0" onClick={() => actions.newChat()} size="sm" variant="outline">
          New chat about {ambientLabel ?? "this patient"}
        </Button>
      </>
    ) : (
      <span className="truncate">
        About <span className="text-foreground">{subjectLabel ?? "this patient"}</span>
      </span>
    );

  return <AgentChat.PromptInput banner={banner} footer={footer} placeholder={placeholder} />;
}
